import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** Was von einem gerollten Bereich zu wissen nötig ist — drei Zahlen, kein DOM. */
export interface Rollmass {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/**
 * ⚠️ **Ein Pixel Spielraum.** Ein gerollter Bereich landet auf iOS selten genau auf der ganzen
 * Zahl: Retina rechnet in Dritteln, `scrollHeight` ist gerundet, und am unteren Anschlag bleibt
 * regelmässig ein halber Pixel übrig. Ohne Spielraum stünde dort ein Schatten, der „es kommt
 * noch etwas" behauptet, obwohl nichts mehr folgt — und ein Schatten, der lügt, ist schlechter
 * als keiner.
 */
const SPIEL = 1;

/**
 * Wo ein gerollter Bereich noch etwas verbirgt: oben, unten, beides oder nichts.
 *
 * Reine Rechnung, damit sie ohne DOM prüfbar ist — das Anhängen an die Ereignisse macht
 * `useRollrand`.
 */
export function rollrand({ scrollTop, scrollHeight, clientHeight }: Rollmass): {
  oben: boolean;
  unten: boolean;
} {
  return {
    oben: scrollTop > SPIEL,
    unten: scrollTop + clientHeight < scrollHeight - SPIEL,
  };
}

/**
 * Hängt die Rechnung an einen Rollbereich und gibt zurück, welche Schatten zu zeigen sind.
 *
 * **Warum nicht nur `onScroll`.** Der Bogen ändert seine Höhe, ohne dass jemand rollt: eine
 * Aufgabe klappt auf, ein Kommentarfeld wächst, die Tastatur kommt. Dann stimmte ein Schatten,
 * der nur beim Rollen nachgerechnet wird, nicht mehr. `ResizeObserver` gibt es auf iOS ab 15.4;
 * darunter bleibt es beim Rollen und beim Wechsel des Inhalts (`abhaengig`) — schlechter, aber
 * nicht kaputt.
 */
export function useRollrand(
  ref: RefObject<HTMLElement | null>,
  abhaengig: unknown,
): { oben: boolean; unten: boolean } {
  const [rand, setRand] = useState({ oben: false, unten: false });
  // Der letzte Stand, ohne Rerender: `scroll` feuert bei jedem Pixel, und ein `setState` mit
  // demselben Wert zeichnet zwar nicht neu, läuft aber durch die ganze Kette.
  const letzter = useRef(rand);

  const messen = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const neu = rollrand(el);
    if (neu.oben === letzter.current.oben && neu.unten === letzter.current.unten) return;
    letzter.current = neu;
    setRand(neu);
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    messen();
    el.addEventListener('scroll', messen, { passive: true });

    const beobachter =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => messen());
    beobachter?.observe(el);
    // Auch die Kinder: die Höhe des Bereichs bleibt gleich, wenn eine Karte darin aufklappt.
    if (beobachter) for (const kind of Array.from(el.children)) beobachter.observe(kind);

    return () => {
      el.removeEventListener('scroll', messen);
      beobachter?.disconnect();
    };
  }, [ref, messen, abhaengig]);

  return rand;
}
